import {
  PermissionGroupUser,
  type PermissionGroupUserList,
  String$,
  User,
  UserAttributes,
  type UserList,
  type UserPropertiesList,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { UserClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type UserFilters = EntityFilter<User>;
export const getUserQueryKeys = (filter: UserFilters) => [
  queryKeys.user.root,
  queryKeys.user.getHash(filter),
];

export const getUserQueryConfig = (filters: UserFilters) => ({
  queryKey: getUserQueryKeys(filters),
  queryFn: () => {
    const userReq = User.create(filters);
    return UserClientService.Get(userReq);
  },
});

export const useUserQuery = <TSelectedData = User,>({
  filters = {},
  enabled = true,
  select,
}: {
  filters?: UserFilters;
  enabled?: boolean;
  select?: (data: Awaited<ReturnType<typeof UserClientService.Get>>) => TSelectedData;
}) => {
  return useQuery({
    ...getUserQueryConfig(filters),
    enabled,
    select,
  });
};

export const getSearchUserQueryConfig = (filters: UserFilters) => ({
  queryKey: getUserQueryKeys(filters),
  queryFn: () => {
    const userReq = User.create(filters);
    return UserClientService.SearchUser(userReq);
  },
});
export const useSearchUserQuery = <TSelectedData = UserList,>({
  filters = {},
  enabled = true,
  select,
  options,
}: {
  filters?: UserFilters;
  enabled?: boolean;
  select?: (data: Awaited<ReturnType<typeof UserClientService.SearchUser>>) => TSelectedData;
  options?: Partial<UseQueryOptions<UserList, Error, TSelectedData>>;
}) => {
  return useQuery({
    ...getSearchUserQueryConfig(filters),
    enabled,
    select,
    ...options,
  });
};

export const useSearchUserPropertiesQuery = <TSelectData = UserPropertiesList,>({
  searchTerm = '',
  enabled = true,
  select,
}: {
  searchTerm?: string;
  enabled?: boolean;
  select?: (data?: UserPropertiesList) => TSelectData;
} = {}) => {
  return useQuery({
    queryFn: () => {
      const req = String$.create({ value: searchTerm });
      return UserClientService.SearchUserProperties(req);
    },
    queryKey: [queryKeys.userProperties.root, queryKeys.userProperties.search, searchTerm],
    enabled: enabled && searchTerm.length >= 2,
    select,
  });
};
export const useUserQueryInline = () => {
  const queryClient = useQueryClient();

  const fetchUser = useCallback(
    async ({ filter = {} }: { filter?: UserFilters } = {}) => {
      const queryKeys = getUserQueryKeys(filter);
      const cache = queryClient.getQueryData<User>(queryKeys);
      if (!cache) {
        const userReq = User.create(filter);
        const user = await UserClientService.Get(userReq);
        queryClient.setQueryData(queryKeys, user);
        return user;
      }
      return cache;
    },
    [queryClient],
  );

  return useMemo(() => ({ fetchUser }), [fetchUser]);
};

export type UserAttributesFilter = EntityFilter<UserAttributes>;
export const useUserAttributesGetQuery = ({
  filter = {},
  enabled,
}: { filter?: UserAttributesFilter; enabled?: boolean } = {}) => {
  return useQuery({
    queryFn: async () => {
      return await UserClientService.GetUserAttributes(UserAttributes.create(filter));
    },
    queryKey: [
      queryKeys.user.root,
      queryKeys.user.userAttributes,
      queryKeys.user.getUserAttributesHash(filter),
    ],
    enabled,
  });
};

export const getBatchUserQueryConfig = (filters: UserFilters) => {
  return {
    queryKey: [queryKeys.user.root, queryKeys.user.list, queryKeys.user.getHash(filters)],
    queryFn: async () => {
      return await UserClientService.BatchGet(User.create(filters));
    },
  };
};
export const useBatchUserQuery = <TSelectData = UserList,>({
  filters,
  enabled,
  select,
  options,
}: {
  enabled?: boolean;
  filters: UserFilters;
  select?: (data: UserList) => TSelectData;
  options?: Partial<UseQueryOptions<UserList, Error, TSelectData>>;
}) => {
  const queryConfig = getBatchUserQueryConfig(filters);
  return useQuery({
    ...queryConfig,
    select,
    enabled,
    ...options,
  });
};

export const useUserUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: User) => {
      return await UserClientService.Update(req);
    },
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.user.root] });
    },
  });
};

export const useUserCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: User) => {
      return await UserClientService.Create(req);
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.user.root] });
    },
  });
};

export const useGetUserManagerQuery = <SelectData = User,>({
  filters = {},
  enabled = true,
  select,
}: {
  filters?: UserFilters;
  enabled?: boolean;
  select?: (data: Awaited<ReturnType<typeof UserClientService.GetUserManager>>) => SelectData;
}) => {
  return useQuery({
    queryFn: () => {
      const userReq = User.create(filters);
      return UserClientService.GetUserManager(userReq);
    },
    queryKey: [queryKeys.usersManager.root, queryKeys.usersManager.getUserManagerHash(filters)],
    enabled,
    select,
  });
};

export const useGetUsersInPermissionGroupQuery = ({
  permissionGroupId,
  enabled,
}: {
  permissionGroupId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryFn: async () => {
      return await UserClientService.GetUserIdsInPermissionGroup(permissionGroupId);
    },
    queryKey: [
      queryKeys.user.root,
      queryKeys.user.list,
      queryKeys.user.byPermissionGroup,
      permissionGroupId,
    ],
    enabled,
  });
};

export const getBatchGetPermissionGroupUserQueryConfig = (
  filters: BatchGetPermissionGroupUserFilter,
) => {
  return {
    queryKey: [
      queryKeys.user.permissionGroupUser.root,
      queryKeys.user.permissionGroupUser.list,
      queryKeys.user.permissionGroupUser.getHash(filters),
    ],
    queryFn: async () => {
      const req = PermissionGroupUser.create(filters);
      return await UserClientService.BatchGetPermissionGroupUser(req);
    },
  };
};

export type BatchGetPermissionGroupUserFilter = EntityFilter<PermissionGroupUser>;
export const useBatchGetPermissionGroupUserQuery = <SelectData = PermissionGroupUserList,>({
  filters,
  enabled,
  select,
}: {
  filters: BatchGetPermissionGroupUserFilter;
  enabled?: boolean;
  select?: (data: PermissionGroupUserList) => SelectData;
}) => {
  return useQuery({
    ...getBatchGetPermissionGroupUserQueryConfig(filters),
    enabled,
    select,
  });
};
